import _ from 'lodash'

import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { translateString } from 'shared-libs/helpers/utils'

import { EditableFieldProps } from './editable-field-json-props'

/**
 * Canonical shape for a single selectable option. Options provided through the
 * uiSchema must be either a plain string/number or an object of this shape;
 * anything else is rejected by {@link normalizeOptions} with a console error.
 * `enumOptions` and `suggestions` in data schemas already conform.
 */
export interface SelectOption {
  label: string
  value: any
}

/**
 * Raw option entry as accepted from the uiSchema before normalization.
 */
export type RawSelectOption = string | number | SelectOption

/**
 * Shared contract for any UI2 field that presents the user with a list of
 * options to select from (e.g. ui2:pickerField, ui2:radioButtonField).
 *
 * All fields that implement this contract MUST use the helpers in this file
 * to resolve and inspect options — do not roll a bespoke resolver.
 */
export interface SelectableFieldProps<T = any> extends EditableFieldProps<T> {
  /**
   * Explicit option list. When provided, this takes precedence over any
   * options defined on the bound data schema. Must be strings/numbers or
   * `{ label, value }` objects — no other shape is supported.
   */
  options?: RawSelectOption[]
  /**
   * When true, the field allows selecting more than one option and its
   * bound value is an array of selected values.
   */
  isMultiSelect?: boolean
  /**
   * Frames stack. Required so the resolver can read the bound data schema
   * when `options` is not supplied via uiSchema.
   */
  frames?: FramesManager
  /**
   * Translation table applied to option labels. Merged over the table on
   * `frames.getContext('translationTable')` — local entries win.
   */
  translationTable?: Record<string, string>
}

/**
 * Shared state shape for tracking the currently selected option(s) inside a
 * selectable field. Scalar for single-select, array for multi-select.
 */
export interface SelectableFieldState {
  selectedOption: SelectOption | SelectOption[] | undefined
}

/**
 * Object-shape schemas denormalize the picked label alongside the value into
 * the stored entry (so downstream consumers can display it without re-resolving
 * against a schema that may have changed). Some legacy schemas use
 * `displayName` instead of `label` — we honor whichever key the schema declares.
 */
const OBJECT_LABEL_KEYS = ['displayName', 'label']

/**
 * Return the effective item schema for a selectable field:
 *   - When the bound data schema is `type: "array"`, options live on `items`.
 *   - Otherwise the schema itself is the item schema.
 */
function getItemSchema(props: Pick<SelectableFieldProps, 'frames'>): any {
  const dataSchema = props.frames?.getContext?.('dataSchema')
  if (!dataSchema) return null
  if (dataSchema.type === 'array' && dataSchema.items) return dataSchema.items
  return dataSchema
}

/**
 * Normalize a raw option list into `{ label, value }[]`. Strings and numbers
 * become `{ label: String(v), value: v }`. Objects must already be
 * `{ label: string, value: any }`; any other shape is logged as a console
 * error and dropped from the result.
 */
export function normalizeOptions(rawOptions: unknown): SelectOption[] {
  if (!_.isArray(rawOptions)) return []
  const result: SelectOption[] = []
  for (const raw of rawOptions) {
    if (_.isString(raw) || _.isNumber(raw)) {
      result.push({ label: String(raw), value: raw })
      continue
    }
    if (_.isPlainObject(raw)) {
      const opt = raw as Partial<SelectOption>
      if (_.isString(opt.label) && 'value' in opt) {
        // Preserve any per-option display extras (e.g. radio's `icon`,
        // `selectedColor`) alongside the canonical `label` / `value` pair.
        result.push({ ...(raw as SelectOption) })
        continue
      }
    }
    // eslint-disable-next-line no-console
    console.error(
      '[ui2 selectable] Unrecognized option shape — expected string, number, or { label: string, value: any }. Dropping entry:',
      raw,
    )
  }
  return result
}

/**
 * Resolve the option list for a selectable field. Resolution stops at the
 * first source that yields options:
 *   1. `props.options` (uiSchema)
 *   2. `itemSchema.suggestions` (bound data schema; `items` when the bound schema is an array)
 *   3. `itemSchema.enumOptions`
 *   4. `itemSchema.enum`
 */
export function resolveSelectOptions<T>(props: SelectableFieldProps<T>): SelectOption[] {
  const { options } = props
  const itemSchema = getItemSchema(props)

  let source: unknown = null
  if (!_.isEmpty(options)) source = options
  else if (!_.isEmpty(itemSchema?.suggestions)) source = itemSchema.suggestions
  else if (!_.isEmpty(itemSchema?.enumOptions)) source = itemSchema.enumOptions
  else if (!_.isEmpty(itemSchema?.enum)) source = itemSchema.enum

  return normalizeOptions(source)
}

/**
 * Unwrap a stored value down to its identity for equality checks. Object-shape
 * entries (`{ value, label }`) collapse to their `value`; scalars pass through.
 * Selection checks compare identities so callers can be agnostic about whether
 * the bound schema stores scalars or `{value, label}` objects.
 */
export function getOptionIdentity(value: unknown): unknown {
  if (value && typeof value === 'object' && 'value' in (value as Record<string, unknown>)) {
    return (value as { value: unknown }).value
  }
  return value
}

/**
 * True when `option` is currently selected given the field's bound `value`.
 * In multi-select mode `value` is treated as an array and any matching entry
 * counts as selected. Comparison is done via {@link getOptionIdentity} so both
 * scalar and `{value, label}` object storage shapes work.
 */
export function isOptionSelected(
  option: SelectOption,
  currentValue: unknown,
  isMultiSelect?: boolean,
): boolean {
  const target = getOptionIdentity(option.value)
  if (isMultiSelect) {
    if (!_.isArray(currentValue)) return false
    return currentValue.some((v) => _.isEqual(getOptionIdentity(v), target))
  }
  return _.isEqual(getOptionIdentity(currentValue), target)
}

/**
 * Look up an option by its raw stored value. Handles both scalar and
 * `{value, label}` object storage shapes via {@link getOptionIdentity}.
 */
export function findOptionByValue(
  options: SelectOption[],
  value: unknown,
): SelectOption | undefined {
  const target = getOptionIdentity(value)
  return options.find((o) => _.isEqual(getOptionIdentity(o.value), target))
}

/**
 * Return the option(s) currently selected by `currentValue`. Always returns an
 * array so callers can render single- and multi-select the same way; use
 * `[0]` for the scalar case.
 */
export function getSelectedOptions(
  options: SelectOption[],
  currentValue: unknown,
  isMultiSelect?: boolean,
): SelectOption[] {
  if (isMultiSelect) {
    if (!_.isArray(currentValue)) return []
    return currentValue
      .map((v) => findOptionByValue(options, v))
      .filter((o): o is SelectOption => !!o)
  }
  const match = findOptionByValue(options, currentValue)
  return match ? [match] : []
}

/**
 * Convert a picked option into the shape the bound data schema expects to
 * store. Scalar-shape schemas store `option.value` as-is; object-shape schemas
 * (`items.type === 'object'`) store `{ value, [labelKey] }` where `labelKey`
 * is whichever of `label` / `displayName` the schema declares — defaulting to
 * `label` when it declares neither.
 */
export function getStoredValueForOption(
  option: SelectOption,
  props: Pick<SelectableFieldProps, 'frames'>,
): any {
  const itemSchema = getItemSchema(props)
  if (itemSchema?.type !== 'object') return option.value
  const schemaProps = itemSchema.properties ?? {}
  const labelKey =
    OBJECT_LABEL_KEYS.find((key) => key in schemaProps) ?? 'label'
  return { value: option.value, [labelKey]: option.label }
}

/**
 * Merge a local translation table over the one on frames. Local entries win.
 */
export function getEffectiveTranslationTable(
  props: Pick<SelectableFieldProps, 'translationTable' | 'frames'>,
): Record<string, string> {
  return _.merge({}, props.frames?.getContext?.('translationTable'), props.translationTable)
}

/**
 * Translate an option's label using the effective translation table.
 */
export function getOptionLabel(
  option: SelectOption,
  translationTable?: Record<string, string>,
): string {
  return translateString(option.label, translationTable) ?? option.label
}
